import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Loader2, MapPin } from 'lucide-react';
import { getVisitEvvReadings, client } from '../../services/api';
import type {
  VisitEvvReadingResponse,
  VisitEvvReadingsView,
  VisitEvvSignatureView,
} from '../../services/api/types.gen';

/** Coordinate as the API actually emits it — see types.gen.ts. */
type Coordinate = VisitEvvReadingResponse['clock_in_latitude'];
import {
  formatDateTimeInZone,
  formatSignedMinutes,
} from '../../utils/dateUtils';

interface EvvReadingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visitId: string;
}

/** `37.7749, -122.4194`, or null when either half is missing. */
const formatCoordinates = (
  latitude: Coordinate,
  longitude: Coordinate
): string | null => {
  if (latitude === null || latitude === undefined) return null;
  if (longitude === null || longitude === undefined) return null;
  return `${latitude}, ${longitude}`;
};

/**
 * Where the device was, in the form a reviewer can actually compare against the
 * patient's address. The device reverse-geocodes at capture time, so prefer that
 * string; raw coordinates are the fallback for when it could not resolve one.
 */
const formatPlace = (
  address: string | null | undefined,
  latitude: Coordinate,
  longitude: Coordinate
): string | null => address ?? formatCoordinates(latitude, longitude);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className='grid grid-cols-[9rem_1fr] gap-x-4 gap-y-1 py-1.5 text-sm'>
    <div className='text-muted-foreground'>{label}</div>
    <div className='min-w-0 break-words'>{children}</div>
  </div>
);

/**
 * One clock reading beside the time the clinician wrote on the form.
 *
 * The form time is the whole point (EMR-325): the dialog covers the visit form,
 * so without it here the reviewer has nothing to compare the reading against.
 */
const ClockComparison: React.FC<{
  label: string;
  readingAt: string | null | undefined;
  formTime: string | null | undefined;
  deltaMinutes: VisitEvvReadingsView['clock_in_delta_minutes'];
  latitude: Coordinate;
  longitude: Coordinate;
  address: VisitEvvReadingResponse['clock_in_address'];
  timeZone: string | null | undefined;
}> = ({
  label,
  readingAt,
  formTime,
  deltaMinutes,
  latitude,
  longitude,
  address,
  timeZone,
}) => {
  const place = formatPlace(address, latitude, longitude);
  return (
    <div className='border-t py-2 first:border-t-0'>
      <Row label={label}>
        {readingAt ? formatDateTimeInZone(readingAt, timeZone) : 'Not recorded'}
      </Row>
      <Row label='On visit form'>
        {formTime ?? 'Not answered'}
        {readingAt && formTime ? (
          <span className='ml-2 text-muted-foreground'>
            ({formatSignedMinutes(deltaMinutes)})
          </span>
        ) : null}
      </Row>
      {place ? <Row label='Location'>{place}</Row> : null}
    </div>
  );
};

/**
 * The authoritative signature capture.
 *
 * Only the most recent one is shown: a re-capture supersedes what came before,
 * so showing every attempt made the dialog grow without telling the reviewer
 * anything they act on. ``earlierCount`` keeps the fact that re-captures
 * happened visible, since that is itself audit-relevant — the superseded rows
 * are still stored and still returned by the read endpoint.
 */
const SignatureCapture: React.FC<{
  signature: VisitEvvSignatureView;
  earlierCount: number;
  timeZone: string | null | undefined;
}> = ({ signature, earlierCount, timeZone }) => {
  const place = formatPlace(
    signature.address,
    signature.latitude,
    signature.longitude
  );
  return (
    <div className='py-3'>
      {signature.signature_url ? (
        <img
          src={signature.signature_url}
          alt='Patient signature'
          className='max-w-full rounded border bg-white'
        />
      ) : (
        <div className='rounded border border-dashed p-3 text-sm text-muted-foreground'>
          Signature image unavailable.
        </div>
      )}
      <div className='mt-1.5 text-xs text-muted-foreground'>
        Captured {formatDateTimeInZone(signature.captured_at, timeZone)}
        {place ? ` · ${place}` : ''}
        {earlierCount > 0
          ? ` · supersedes ${earlierCount} earlier ${
              earlierCount === 1 ? 'capture' : 'captures'
            }`
          : ''}
      </div>
    </div>
  );
};

/**
 * Read-only view of a visit's EVV readings (EMR-325).
 *
 * Shows what the clinician's device recorded — clock in/out time, location, and
 * the latest patient signature — beside what we independently know: the times
 * written on the visit form and the patient's home address. The point is to make
 * a discrepancy visible; the reviewer decides what it means, so deltas are
 * stated plainly with no pass/fail styling.
 *
 * Times render in the agency's timezone, not the app-wide Pacific default: the
 * deltas are computed in that zone server-side, and a reader comparing against a
 * locally written form time would otherwise be handed two different clocks.
 */
export const EvvReadingsDialog: React.FC<EvvReadingsDialogProps> = ({
  open,
  onOpenChange,
  visitId,
}) => {
  const [data, setData] = useState<VisitEvvReadingsView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getVisitEvvReadings({ path: { visit_id: visitId }, client })
      .then(res => {
        if (cancelled) return;
        // The client is not configured with `throwOnError`, so HTTP failures
        // resolve with a populated `error` instead of rejecting. Without this
        // branch a 500 fell through as `data: null` and the panel claimed no EVV
        // was captured — the opposite of the truth, on a compliance view.
        if (res.error || !res.data) {
          console.error('Failed to load EVV readings:', res.error);
          setError('Unable to load EVV readings. Please try again.');
          return;
        }
        setData(res.data);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load EVV readings:', err);
        setError('Unable to load EVV readings. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, visitId]);

  const reading = data?.reading ?? null;
  const signatures = data?.signatures ?? [];
  // The read endpoint orders captures newest-first, so index 0 is the
  // authoritative one. Deliberately not re-sorted here: one ordering rule, and
  // it lives with the query.
  const latestSignature = signatures[0] ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[560px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <MapPin className='h-5 w-5 text-primary' />
            EVV readings
          </DialogTitle>
          <DialogDescription>
            Captured by the clinician&apos;s device, shown against the visit
            form and the patient&apos;s address on file.
          </DialogDescription>
        </DialogHeader>
        <div className='max-h-[60vh] overflow-y-auto pr-1'>
          {loading ? (
            <div className='flex items-center justify-center py-8 text-sm text-muted-foreground'>
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              Loading...
            </div>
          ) : error ? (
            <div className='py-6 text-sm text-destructive'>{error}</div>
          ) : !data?.has_evv_data ? (
            <div className='py-6 text-sm text-muted-foreground'>
              No EVV was captured for this visit.
            </div>
          ) : (
            <>
              <ClockComparison
                label='Clock in'
                readingAt={reading?.clock_in_at}
                formTime={data.form_time_in}
                deltaMinutes={data.clock_in_delta_minutes}
                latitude={reading?.clock_in_latitude}
                longitude={reading?.clock_in_longitude}
                address={reading?.clock_in_address}
                timeZone={data.agency_timezone}
              />
              <ClockComparison
                label='Clock out'
                readingAt={reading?.clock_out_at}
                formTime={data.form_time_out}
                deltaMinutes={data.clock_out_delta_minutes}
                latitude={reading?.clock_out_latitude}
                longitude={reading?.clock_out_longitude}
                address={reading?.clock_out_address}
                timeZone={data.agency_timezone}
              />

              <div className='border-t py-2'>
                <Row label='Home address'>
                  {data.patient_address ?? 'Not on file'}
                </Row>
              </div>

              <div className='border-t pt-3'>
                <div className='mb-1 text-sm font-medium'>
                  Patient signature
                </div>
                {latestSignature === null ? (
                  <div className='py-2 text-sm text-muted-foreground'>
                    No signature was captured.
                  </div>
                ) : (
                  <SignatureCapture
                    signature={latestSignature}
                    earlierCount={signatures.length - 1}
                    timeZone={data.agency_timezone}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
